import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDirective as _resolveDirective, createVNode as _createVNode, withDirectives as _withDirectives, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "row items-center justify-end" }
const _hoisted_2 = { class: "row items-center justify-end" }

import { date as qdate, is } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnUndo from "src/components/button/SfxBtnUndo.vue"
import SfxInput from "src/components/input/SfxInput.vue"
import { DATE_MASK, DATE_MASK_FORWARD_SLASH_SEPARATOR } from "src/util/constants/configurations"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { isEmptyOrNull } from "src/util/functions/misc"
import { computed } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits
interface Props {
  label: string
  date?: string | null
  time?: string | null
  required?: boolean
  disable?: boolean
  readonly?: boolean
  enable_date?: boolean
  enable_time?: boolean
  showTime?: boolean
  originalValueDate?: string | null
  originalValueTime?: string | null
  startDate?: string
  startTime?: string
  color?: string
  events?: string[]
  eventColor?: string
}

const timeMask = "HH:mm"


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxTimeEdit',
  props: {
    label: {},
    date: {},
    time: {},
    required: { type: Boolean, default: false },
    disable: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    enable_date: { type: Boolean, default: true },
    enable_time: { type: Boolean, default: true },
    showTime: { type: Boolean, default: true },
    originalValueDate: {},
    originalValueTime: {},
    startDate: {},
    startTime: {},
    color: { default: "info" },
    events: { default: () => [] },
    eventColor: { default: "info" }
  },
  emits: ["update:date", "update:time"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

const { t } = useI18n()
const labelClose = t("sfxButtons.btn_done").toUpperCase()

const inputMask = computed(() => {
  let inputMaskString = ""

  if (props.enable_date) inputMaskString += "####-##-## "
  if (props.enable_time) inputMaskString += "##:##"

  // Make sure to remove the extra space if only date
  return inputMaskString.trim()
})

const dateModel = computed<string | null>({
  get: () => props.date || null,

  set: (value) => {
    if (value === "") value = null
    emit("update:date", value)
  },
})

const timeModel = computed<string | null>({
  get: () => props.time || null,

  set: (value) => {
    if (value === "") value = null
    emit("update:time", value)
  },
})

const inputModel = computed({
  get: () => {
    if (props.enable_date && props.enable_time) {
      if (dateModel.value && timeModel.value) return `${dateModel.value} ${timeModel.value}`
      else if (dateModel.value) return dateModel.value
      else if (timeModel.value) return timeModel.value
    } else if (props.enable_date) return dateModel.value
    else if (props.enable_time) return timeModel.value
    return null
  },

  set: (value) => {
    if (props.enable_date && props.enable_time) {
      const dateTime = value?.split(" ") || [null, null]
      dateModel.value = dateTime[0]
      timeModel.value = dateTime[1]
    } else if (props.enable_date) dateModel.value = value || null
    else if (props.enable_time) timeModel.value = value || null
  },
})

const originalValueModel = computed(() => {
  if (props.originalValueDate && props.originalValueTime)
    return `${props.originalValueDate} ${props.originalValueTime}`
  else if (props.originalValueDate) return props.originalValueDate
  else if (props.originalValueTime) return props.originalValueTime
  return null
})

const validDateTimeRules = computed(() => {
  if (props.enable_date && props.enable_time) {
    return [
      (val: string | null) =>
        isEmptyOrNull(val) || isValidDate(val) || t("sfxTimeEdit.error_date_time"),
    ]
  } else if (props.enable_date) {
    return [
      (val: string | null) => isEmptyOrNull(val) || isValidDate(val) || t("sfxTimeEdit.error_date"),
    ]
  } else if (props.enable_time) {
    return [
      (val: string) =>
        isEmptyOrNull(val) || isValidDate(addDateToClock(val)) || t("sfxTimeEdit.error_time"),
    ]
  }
  return []
})

const isEqualToOriginal = computed(() => {
  // In case one of them is not defined, we make it null, so it is same to the model values null value
  const sameDate = is.deepEqual(props.originalValueDate || null, dateModel.value)
  // Only compare hours and minutes because of the input mask.
  const sameTime =
    (props.originalValueTime?.slice(0, timeMask.length) ?? null) ===
    (timeModel.value?.slice(0, timeMask.length) ?? null)

  return sameDate && sameTime
})

const edited = computed(
  () => originalValueModel.value && !props.disable && !isEqualToOriginal.value
)

const readonlyField = computed(() => props.readonly)
const disableField = computed(() => props.disable || (disableCalendar.value && disableClock.value))

const disableClock = computed(() => props.disable || !props.enable_time)
const disableCalendar = computed(() => props.disable || !props.enable_date)

const clockClass = computed(() => {
  return { "cursor-pointer": !disableClock.value, "text-navy-base": !disableClock.value }
})
const calendarClass = computed(() => {
  return { "cursor-pointer": !disableCalendar.value, "text-navy-base": !disableCalendar.value }
})

const highlightedEvents = computed<string[]>(() => {
  return props.events.map((event) => {
    return qdate.formatDate(event, DATE_MASK_FORWARD_SLASH_SEPARATOR)
  })
})

//endregion

//region Functions

function isValidDate(dateVal: string | null) {
  // Mask is 2023-02-09 13:20 - 16 characters.
  if (dateVal == null || dateVal.length === 0) {
    return true
  } else if (props.enable_time) {
    // To avoid stupid JS stuff, we with seconds conversion, we make sure that seconds are either both there or both gone
    return qdate.isValid(dateVal) && dateVal.length !== 15
  } else if (props.enable_date) {
    return qdate.isValid(dateVal)
  } else {
    return false
  }
}

function addDateToClock(value: string) {
  return qdate.formatDate(Date.now(), "YYYY-MM-DD") + " " + value
}

function updateTime(newTime: string | null) {
  // If the user pressed the now button without giving any date, then take the current
  if (newTime && props.enable_date && !dateModel.value) {
    inputModel.value = addDateToClock(newTime)
  } else {
    timeModel.value = newTime || null
  }
}

function startDateOptions(dateOption: string) {
  const formattedDate = qdate.formatDate(props.startDate, DATE_MASK_FORWARD_SLASH_SEPARATOR)
  return props.startDate ? formattedDate <= dateOption : true
}

function startTimeOptions(hr: number, min: number | null) {
  if (!props.startTime) return true

  const hour = Number(props.startTime.slice(0, 2))
  const minute = Number(props.startTime.slice(-2))

  if (hr < hour) return false

  return min === null || min > minute
}

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_date = _resolveComponent("q-date")!
  const _component_q_popup_proxy = _resolveComponent("q-popup-proxy")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_time = _resolveComponent("q-time")!
  const _directive_close_popup = _resolveDirective("close-popup")!

  return (_openBlock(), _createBlock(SfxInput, {
    modelValue: inputModel.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((inputModel).value = $event)),
    class: "q-mb-xs",
    label: props.label,
    mask: inputMask.value,
    required: props.required,
    disable: disableField.value,
    rules: validDateTimeRules.value,
    "hide-bottom-space": "",
    "is-original-value": isEqualToOriginal.value,
    readonly: readonlyField.value
  }, _createSlots({ _: 2 }, [
    (!readonlyField.value)
      ? {
          name: "prepend",
          fn: _withCtx(() => [
            _createVNode(_component_q_icon, {
              name: _unref(Icons).OPEN_CALENDAR,
              class: _normalizeClass(calendarClass.value),
              disable: disableCalendar.value
            }, {
              default: _withCtx(() => [
                _createVNode(_component_q_popup_proxy, {
                  cover: "",
                  "transition-show": "scale",
                  "transition-hide": "scale",
                  target: !disableCalendar.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_q_date, {
                      modelValue: dateModel.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateModel).value = $event)),
                      mask: _unref(DATE_MASK),
                      options: startDateOptions,
                      color: _ctx.color,
                      events: highlightedEvents.value,
                      "event-color": _ctx.eventColor,
                      "first-day-of-week": "1",
                      "today-btn": ""
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                          _withDirectives(_createVNode(SfxBtn, {
                            label: _unref(labelClose),
                            flat: ""
                          }, null, 8, ["label"]), [
                            [_directive_close_popup]
                          ])
                        ])
                      ]),
                      _: 1
                    }, 8, ["modelValue", "mask", "color", "events", "event-color"])
                  ]),
                  _: 1
                }, 8, ["target"])
              ]),
              _: 1
            }, 8, ["name", "class", "disable"])
          ]),
          key: "0"
        }
      : undefined,
    (_ctx.showTime && !readonlyField.value)
      ? {
          name: "append",
          fn: _withCtx(() => [
            _createVNode(_component_q_icon, {
              name: _unref(Icons).CLOCK,
              class: _normalizeClass(clockClass.value),
              disable: disableClock.value
            }, {
              default: _withCtx(() => [
                _createVNode(_component_q_popup_proxy, {
                  cover: "",
                  "transition-show": "scale",
                  "transition-hide": "scale",
                  target: !disableClock.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_q_time, {
                      "model-value": timeModel.value,
                      "onUpdate:modelValue": updateTime,
                      mask: timeMask,
                      options: startTimeOptions,
                      color: _ctx.color,
                      format24h: "",
                      "now-btn": ""
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                          _withDirectives(_createVNode(SfxBtn, {
                            label: _unref(labelClose),
                            flat: ""
                          }, null, 8, ["label"]), [
                            [_directive_close_popup]
                          ])
                        ])
                      ]),
                      _: 1
                    }, 8, ["model-value", "color"])
                  ]),
                  _: 1
                }, 8, ["target"])
              ]),
              _: 1
            }, 8, ["name", "class", "disable"])
          ]),
          key: "1"
        }
      : undefined,
    (originalValueModel.value)
      ? {
          name: "after",
          fn: _withCtx(() => [
            (edited.value)
              ? (_openBlock(), _createBlock(SfxBtnUndo, {
                  key: 0,
                  "original-value": originalValueModel.value,
                  modelValue: inputModel.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((inputModel).value = $event))
                }, null, 8, ["original-value", "modelValue"]))
              : _createCommentVNode("", true)
          ]),
          key: "2"
        }
      : undefined
  ]), 1032, ["modelValue", "label", "mask", "required", "disable", "rules", "is-original-value", "readonly"]))
}
}

})